import axiosInstance from "@/api/axios-instance";
import { getApiErrorMessage } from "@/helpers/get-api-error-message";
import { Role, roleDisplayMap } from "@/helpers/role-permission";
import { toast } from "@/hooks/use-toast";
import { describeAccessChange } from "../ui/access-change-toast";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { classifyAccessError } from "@/components/common/access-unavailable";
import { AxiosError } from "axios";
import { useLayoutEffect, useRef } from "react";
import {
  AccessMatrix,
  Capability,
  CrmArea,
  MyAccess,
  getAccessMatrix,
  getMyAccess,
  setAccessCell,
} from "../services/access";

const MATRIX_KEY = ["access-matrix"];

const POLL_EVERY_MS = 5000;
const POLL_ATTEMPTS = 12;

/**
 * The caller's own access, read from the server rather than from Clerk metadata.
 *
 * This is what stops the client and the server disagreeing: grants are set per
 * role in the database, so a UI that kept reading each user's Clerk metadata
 * would show buttons that 403 the moment anyone ticks a box.
 */
export function useMyAccess() {
  return useQuery({
    queryKey: ["my-access"],
    queryFn: getMyAccess,
    // Re-ask when the tab comes back. PermissionProvider is the only observer
    // and it sits on the outermost layout route, so without this the answer is
    // fetched once per page load and navigating never refreshes it: an admin
    // who unticks an area for your role does not reach your open tab, and you
    // walk into a page that renders from the stale map and fills with 403s.
    refetchOnWindowFocus: true,
    // Which makes this the throttle, and the reason it is not longer: at most
    // one refetch per minute per tab however often you alt-tab. Long enough
    // that switching between windows costs nothing, short enough that an access
    // change lands while someone is still reading the mail that announced it.
    staleTime: 1000 * 60,
    // A denial is an answer, not a blip — retrying a 401 or a 403 only delays
    // the screen that explains it. A request that never landed is worth two
    // more tries before we tell someone the CRM is unreachable.
    retry: (failureCount, error) =>
      failureCount < 2 && classifyAccessError(error) === "unavailable",
    // The one failure that fixes itself: a staff record the Clerk user.created
    // webhook has not written yet. Poll for a minute, which covers the usual
    // second or two plus a retry, then stop and leave the Try again button.
    refetchInterval: (query) =>
      classifyAccessError(query.state.error) === "not-set-up" &&
      query.state.errorUpdateCount <= POLL_ATTEMPTS
        ? POLL_EVERY_MS
        : false,
  });
}

/**
 * Whether a failed request means "ask the server what I may do now".
 *
 * Pure, and exported for its test: the second condition is the whole safety of
 * the interceptor below, and it is worth pinning directly rather than through a
 * rendered tree.
 *
 * 401 and 403 are the two answers that can mean the grid moved underneath us —
 * an area unticked, a role swapped, an account removed. A 500, a validation
 * 400, a request that never landed: none of those say anything about access.
 */
export function shouldRefreshAccess(error: unknown): boolean {
  if (!(error instanceof AxiosError)) return false;

  const status = error.response?.status;
  if (status !== 401 && status !== 403) return false;

  // Never on /me's own answer. Invalidating the query that just failed refetches
  // it, which fails the same way, which invalidates again — and invalidation
  // ignores staleTime, so nothing throttles that loop. /me is the authority
  // here: it gets asked, it never asks.
  return !(error.config?.url ?? "").includes("/internal/access/me");
}

/**
 * Re-ask for the caller's access whenever a request is refused.
 *
 * The complement of refetchOnWindowFocus: that covers coming back to the tab,
 * this covers finding out the hard way. A page opened from a stale map answers
 * 403, /me is asked again, and AreaRoute sends the person somewhere their role
 * can actually go — instead of leaving a page shell filling with errors, which
 * reads as a broken CRM rather than as access they no longer have.
 *
 * It decides nothing by itself, and that is the point: it only asks the
 * authority to answer again, while what the screen does stays driven by /me's
 * own response through classifyAccessError. So a 401 from a token that expired
 * mid-flight resolves to a fresh 200 and nothing visible happens.
 */
export function useRefreshAccessOnDenial() {
  const queryClient = useQueryClient();
  const interceptorIdRef = useRef<number | null>(null);

  useLayoutEffect(() => {
    // Same shape as the request interceptor in ClerkAuthSessionProvider,
    // including the guard against registering twice.
    if (interceptorIdRef.current !== null) {
      axiosInstance.interceptors.response.eject(interceptorIdRef.current);
    }

    interceptorIdRef.current = axiosInstance.interceptors.response.use(
      (response) => response,
      (error) => {
        // Only once /me has answered at least once. During boot its first fetch
        // is already in flight and will give the authoritative answer; asking
        // again here would only duplicate it.
        if (
          shouldRefreshAccess(error) &&
          queryClient.getQueryData<MyAccess>(["my-access"])
        ) {
          void queryClient.invalidateQueries({ queryKey: ["my-access"] });
        }
        // Never swallowed: every caller still sees its own failure.
        return Promise.reject(error);
      },
    );

    return () => {
      if (interceptorIdRef.current !== null) {
        axiosInstance.interceptors.response.eject(interceptorIdRef.current);
        interceptorIdRef.current = null;
      }
    };
  }, [queryClient]);
}

export function useAccessMatrix() {
  return useQuery({
    queryKey: MATRIX_KEY,
    queryFn: getAccessMatrix,
    // Someone else may be editing the same screen; converge when refocused.
    refetchOnWindowFocus: true,
    staleTime: 0,
  });
}

/**
 * `groupLabel` is UI-only and never reaches the server: it marks a write as one
 * of several fired by a group switch, so this hook stays quiet and the grid can
 * announce the group once. Without it a click on Users produces a toast per
 * page underneath it, naming pages the operator never touched.
 */
type SetCellVars = Parameters<typeof setAccessCell>[0] & { groupLabel?: string };

export function useSetAccessCell() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ groupLabel: _groupLabel, ...cell }: SetCellVars) =>
      setAccessCell(cell),

    // Optimistic, because there is no save button and a spinner on each of ~108
    // checkboxes would be unreadable. The snapshot is what makes the revert
    // possible if the write is refused.
    onMutate: async (cell) => {
      await queryClient.cancelQueries({ queryKey: MATRIX_KEY });
      const previous = queryClient.getQueryData<AccessMatrix>(MATRIX_KEY);

      queryClient.setQueryData<AccessMatrix>(MATRIX_KEY, (old) => {
        if (!old) return old;
        return {
          ...old,
          roles: old.roles.map((role) =>
            role.name !== cell.role
              ? role
              : {
                  ...role,
                  cells: {
                    ...role.cells,
                    [cell.area]: {
                      ...role.cells[cell.area],
                      [cell.capability]: cell.value,
                    },
                  },
                },
          ),
        };
      });

      return { previous };
    },

    onError: (err, _cell, context) => {
      if (context?.previous) {
        queryClient.setQueryData(MATRIX_KEY, context.previous);
      }
      toast({
        title: "Access not changed",
        description: getApiErrorMessage(err, "Could not save that change."),
        variant: "destructive",
      });
    },

    // Absorb the version the server just wrote. Without this, toggling the same
    // cell twice before the refetch lands sends the ORIGINAL version and gets a
    // spurious 409 blaming a concurrent editor who does not exist.
    //
    // can_view/can_edit are echoed too, because the server enforces "Edit implies
    // View" and may have changed the other half of the pair.
    onSuccess: (result, cell, context) => {
      queryClient.setQueryData<AccessMatrix>(MATRIX_KEY, (old) => {
        if (!old || !result?.version) return old;
        return {
          ...old,
          roles: old.roles.map((role) =>
            role.name !== cell.role
              ? role
              : {
                  ...role,
                  cells: {
                    ...role.cells,
                    [cell.area]: {
                      ...role.cells[cell.area],
                      ...(result.access ?? {}),
                      version: result.version,
                    },
                  },
                },
          ),
        };
      });

      // Nothing was written and no audit row exists, so say nothing. A toast here
      // would claim a change that did not happen.
      if (result?.unchanged) return;

      // One of a group switch's writes. The grid announces the group once, after
      // they all land — saying "Decorator Links revoked" when the operator
      // clicked Users names a page they never touched.
      if (cell.groupLabel) return;

      const before = context?.previous;
      const previousCell = before?.roles.find((r) => r.name === cell.role)
        ?.cells[cell.area];
      const areaLabel =
        before?.areas.find((a) => a.key === cell.area)?.label ?? cell.area;
      const roleLabel = roleDisplayMap[cell.role as Role] ?? cell.role;

      toast({
        ...describeAccessChange({
          roleLabel,
          areaLabel,
          before: previousCell,
          after: result.access,
          granted: cell.value,
        }),
        className: "border-orange-500",
      });
    },

    onSettled: (_result, _error, cell) => {
      queryClient.invalidateQueries({ queryKey: MATRIX_KEY });
      // The caller's own access changes only when it was THEIR role that was
      // toggled; refetching /me for every other row is a request that can only
      // fail. (Unknown own role: refetch, to stay on the safe side.)
      const mine = queryClient.getQueryData<MyAccess>(["my-access"]);
      if (!mine?.role || mine.role === cell.role) {
        queryClient.invalidateQueries({ queryKey: ["my-access"] });
      }
    },
  });
}

export type { AccessMatrix, Capability, CrmArea };
